import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api } from "../App";
import MessagePanel from "../components/MessagePanel";
import { LoadingOverlay } from "../components/LoadingOverlay";
import {
  AssignOfficialModal,
  SURVEY_WORKFLOW_STATUSES,
  REVENUE_WORKFLOW_STATUSES,
  getDesignationLabel as sharedGetDesignationLabel,
  getLocationInfo as sharedGetLocationInfo,
} from "../components/AssignOfficialModal";

// Import refactored components
import {
  ApplicationHeader,
  ApplicationInfoCard,
  CommentsSection,
  DocumentsSection,
  ActionsCard,
  StatusTimeline,
  TransferDialog,
  OverrideDialog,
  SectionDialog,
  EditDialog,
  DeleteDialog,
} from "../components/application-detail";

// Status flows - New 9-step workflow:
// Submitted → Assigned → Document Verification → Field Verification → 
// Under Review → Survey Inspection → Boundary Verification → 
// Sketch Preparation → Order / Endorsement Issued
const revenueStatusFlow = [
  { value: "submitted", label: "Submitted" },
  { value: "assigned", label: "Assigned" },
  { value: "document_verification", label: "Document Verification" },
  { value: "field_verification", label: "Field Verification" },
  { value: "under_review", label: "Under Review" },
  { value: "survey_inspection", label: "Survey Inspection" },
  { value: "boundary_verification", label: "Boundary Verification" },
  { value: "sketch_preparation", label: "Sketch Preparation" },
  { value: "order_issued", label: "Order / Endorsement Issued" },
];

const surveyStatusFlow = [
  { value: "submitted", label: "Submitted" },
  { value: "assigned", label: "Assigned" },
  { value: "survey_inspection", label: "Survey Inspection" },
  { value: "boundary_verification", label: "Boundary Verification" },
  { value: "sketch_preparation", label: "Sketch Preparation" },
  { value: "order_issued", label: "Order / Endorsement Issued" },
];

const bothStatusFlow = [
  { value: "submitted", label: "Submitted", phase: "revenue" },
  { value: "assigned", label: "Assigned", phase: "revenue" },
  { value: "document_verification", label: "Document Verification", phase: "revenue" },
  { value: "field_verification", label: "Field Verification", phase: "revenue" },
  { value: "under_review", label: "Under Review", phase: "revenue" },
  { value: "revenue_approved", label: "Revenue Approved", phase: "revenue" },
  { value: "survey_inspection", label: "Survey Inspection", phase: "survey" },
  { value: "boundary_verification", label: "Boundary Verification", phase: "survey" },
  { value: "sketch_preparation", label: "Sketch Preparation", phase: "survey" },
  { value: "order_issued", label: "Order / Endorsement Issued", phase: "survey" },
];

// Section and Circle options
const SECTIONS = [
  { value: "lnd", label: "LND" },
  { value: "rrt", label: "RRT" },
  { value: "lrf", label: "LRF" },
  { value: "nada_kacheri", label: "Nada Kacheri" },
  { value: "survey", label: "Survey" },
  { value: "bhoomi", label: "Bhoomi" },
  { value: "94c", label: "94C" },
  { value: "ssy", label: "SSY" },
  { value: "abhi_lekhalaya_shake", label: "Abhi Lekhalaya Shake" },
  { value: "eo", label: "EO" },
  { value: "ncr", label: "NCR" },
  { value: "ddlr", label: "DDLR" },
  { value: "laq", label: "LAQ" },
  { value: "hoa", label: "HOA" },
];

const CIRCLES = [
  { value: "hullena_halli", label: "Hullena Halli" },
  { value: "hebbalalu", label: "Hebbalalu" },
  { value: "baginagere", label: "Baginagere" },
  { value: "sanki_ghatta", label: "Sanki Ghatta" },
  { value: "nerelekere", label: "Nerelekere" },
  { value: "tippasandra", label: "Tippasandra" },
  { value: "dodda_somanahalli", label: "Dodda Somanahalli" },
  { value: "mallasandra", label: "Mallasandra" },
  { value: "chikka_mudigere", label: "Chikka Mudigere" },
  { value: "mayasandra", label: "Mayasandra" },
  { value: "chikkahalli", label: "Chikkahalli" },
];

// Survey Office Types - for Survey department application assignment
const SURVEY_OFFICE_TYPES = [
  { value: "survey_office", label: "Survey (ADLR) Office" },
  { value: "field_office", label: "Field Office" },
];

const GOK_DESIGNATIONS = ["ac", "dc", "gok_secretary", "principal_secretary", "minister"];
const SUPER_AUTHORITIES = ["tahsildar", "ddlr", "adlr"];
// Survey Department designations - Circle doesn't apply to these
const SURVEY_DESIGNATIONS = ['ddlr', 'adlr', 'surveyor', 'case_worker', 'superintendent', 'survey_supervisor'];

export default function ApplicationDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // RBAC: Check if user is from Survey Department (Circle doesn't apply to Survey)
  const isSurveyUser = user?.department === 'survey' || SURVEY_DESIGNATIONS.includes(user?.designation);
  const showCircleField = !isSurveyUser;

  // Core state
  const [application, setApplication] = useState(null);
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validStatusData, setValidStatusData] = useState(null);

  // Dialog states
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Transfer state
  const [transferOfficials, setTransferOfficials] = useState([]);
  const [selectedTransferOfficial, setSelectedTransferOfficial] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  // Override state
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Section assignment state
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedSurveyOfficeType, setSelectedSurveyOfficeType] = useState("");
  const [sectionComment, setSectionComment] = useState("");
  const [sectionLoading, setSectionLoading] = useState(false);

  // Edit state
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Assign to Official state (for Survey super authorities)
  const [showAssignOfficialDialog, setShowAssignOfficialDialog] = useState(false);
  const [assignableOfficials, setAssignableOfficials] = useState([]);
  const [selectedAssignOfficial, setSelectedAssignOfficial] = useState("");
  const [assignOfficialLoading, setAssignOfficialLoading] = useState(false);
  const [officialSearchQuery, setOfficialSearchQuery] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assignToStatus, setAssignToStatus] = useState("");

  // Get workflow statuses based on user's department (imported from AssignOfficialModal)
  const workflowStatuses = isSurveyUser ? SURVEY_WORKFLOW_STATUSES : REVENUE_WORKFLOW_STATUSES;

  // Survey Office designations (work at Survey Office, not in the field)
  const SURVEY_OFFICE_DESIGNATIONS = ['adlr', 'case_worker', 'survey_supervisor', 'superintendent'];
  // Field Office designations (work in the field)
  const SURVEY_FIELD_DESIGNATIONS = ['surveyor', 'taluk_surveyor'];
  // All Survey designations
  const SURVEY_DESIGNATIONS_FOR_FILTER = ['ddlr', 'adlr', 'surveyor', 'taluk_surveyor', 'case_worker', 'superintendent', 'survey_supervisor'];

  // Translated section/circle/survey office options
  const sections = SECTIONS.map((s) => ({
    value: s.value,
    label: t(`section.${s.value}`) || s.label,
  }));

  const circles = CIRCLES.map((c) => ({
    value: c.value,
    label: t(`circle.${c.value}`) || c.label,
  }));

  const surveyOfficeTypes = SURVEY_OFFICE_TYPES.map((o) => ({
    value: o.value,
    label: t(`surveyOffice.${o.value}`) || o.label,
  }));

  // Use shared helpers from AssignOfficialModal
  const getDesignationLabel = (official) => sharedGetDesignationLabel(official);
  const getLocationInfo = (official) => sharedGetLocationInfo(official);

  // Categorize officials into Office and Field categories
  // For Revenue: Field officials are filtered by application's Hobli/Circle/Village
  const { officeOfficials, fieldOfficials } = useMemo(() => {
    const office = [];
    const field = [];
    
    // Get application location for filtering
    const appHobli = application?.hobli?.toUpperCase() || '';
    const appCircle = application?.circle?.toUpperCase() || '';
    const appVillage = application?.village?.toUpperCase() || '';
    
    assignableOfficials.forEach(official => {
      if (isSurveyUser) {
        // For Survey users, ONLY show Survey department officials
        if (official.department !== 'survey') return;
        
        // Categorize by designation
        if (SURVEY_FIELD_DESIGNATIONS.includes(official.designation)) {
          field.push(official);
        } else {
          office.push(official);
        }
      } else {
        // For Revenue users, categorize by field assignment
        const hasFieldAssignment = 
          (official.assigned_hobli && official.assigned_hobli !== '') ||
          (official.circle_assignments && official.circle_assignments.length > 0);
        
        if (hasFieldAssignment) {
          // Only include Field Officials whose assigned location matches the application's location
          const officialHobli = official.assigned_hobli?.toUpperCase() || '';
          // circle_assignments can be array of objects {circle: "...", hobli: "..."} or strings
          const officialCircles = (official.circle_assignments || []).map(c => {
            if (typeof c === 'string') return c.toUpperCase();
            if (c && typeof c === 'object' && c.circle) return c.circle.toUpperCase();
            return '';
          }).filter(Boolean);
          
          // Check if official's hobli matches application's hobli
          const hobliMatches = !appHobli || !officialHobli || officialHobli === appHobli;
          
          // Check if any of official's circles match application's circle
          const circleMatches = !appCircle || officialCircles.length === 0 || officialCircles.includes(appCircle);
          
          // Include if location matches (or if application has no location data)
          if (hobliMatches && circleMatches) {
            field.push(official);
          }
        } else {
          office.push(official);
        }
      }
    });
    
    return { officeOfficials: office, fieldOfficials: field };
  }, [assignableOfficials, isSurveyUser, application]);

  // Filter officials based on search query
  const filteredOfficeOfficials = useMemo(() => {
    if (!officialSearchQuery.trim()) return officeOfficials;
    const query = officialSearchQuery.toLowerCase();
    return officeOfficials.filter(o => 
      o.name?.toLowerCase().includes(query) || 
      o.designation?.toLowerCase().includes(query)
    );
  }, [officeOfficials, officialSearchQuery]);

  const filteredFieldOfficials = useMemo(() => {
    if (!officialSearchQuery.trim()) return fieldOfficials;
    const query = officialSearchQuery.toLowerCase();
    return fieldOfficials.filter(o => 
      o.name?.toLowerCase().includes(query) || 
      o.designation?.toLowerCase().includes(query)
    );
  }, [fieldOfficials, officialSearchQuery]);

  // Helper functions
  const getStatusFlow = () => {
    if (application?.department === "both") return bothStatusFlow;
    if (application?.department === "survey") return surveyStatusFlow;
    return revenueStatusFlow;
  };

  const getCurrentPhase = () => {
    if (!application || application.department !== "both") return null;
    const revenueStatuses = ["submitted", "assigned", "document_verification", "field_verification", "under_review"];
    return revenueStatuses.includes(application.status) ? "revenue" : "survey";
  };

  const isGOKOfficial = () => {
    return user?.designation && GOK_DESIGNATIONS.includes(user.designation);
  };

  const canApprove = () => {
    if (!user || !application) return false;
    const appDept = application.department || "revenue";
    if (appDept === "both") {
      return user.designation === "ddlr" && application.status === "order_issued";
    }
    if (appDept === "survey") return user.designation === "ddlr";
    return user.designation === "tahsildar";
  };

  const canTransferDepartment = () => {
    if (!user || !application) return false;
    // Tahsildar can transfer to Survey, DDLR/ADLR can transfer to Revenue
    return user.designation === "tahsildar" || user.designation === "ddlr" || user.designation === "adlr";
  };

  const canAssignSection = () => {
    if (!user || !application) return false;
    // Tahsildar can assign section/circle for Revenue applications
    if (user.designation === "tahsildar") return true;
    // ADLR/DDLR can assign office type for Survey applications
    if (["adlr", "ddlr"].includes(user.designation) && application.department === "survey") return true;
    return false;
  };

  // Check if user is a super authority (Tahsildar, DDLR, ADLR)
  const isSuperAuthority = () => {
    if (!user) return false;
    return SUPER_AUTHORITIES.includes(user.designation);
  };

  // Check if user can assign application to an official (for super authorities)
  const canAssignOfficial = () => {
    if (!user || !application) return false;
    // Super authorities can assign unassigned applications or reassign applications
    if (!isSuperAuthority()) return false;
    
    // Check if the application is in a state that can be assigned
    const assignableStatuses = ["submitted", "assigned", "document_verification", "field_verification"];
    if (!assignableStatuses.includes(application.status)) return false;
    
    // Revenue super authority (Tahsildar) can assign Revenue department applications
    if (user.designation === "tahsildar" && 
        (application.department === "revenue" || application.department === "both")) {
      return true;
    }
    
    // Survey super authorities (DDLR/ADLR) can assign Survey department applications
    // or applications that have been routed to Survey sections
    if ((user.designation === "ddlr" || user.designation === "adlr") && 
        (application.department === "survey" || 
         ["ddlr", "survey"].includes(application.section))) {
      return true;
    }
    
    return false;
  };

  const getTargetDepartment = () => {
    if (user?.designation === "tahsildar") return "survey";
    if (user?.designation === "ddlr" || user?.designation === "adlr") return "revenue";
    return null;
  };

  const canApproveRevenuePhase = () => {
    if (!user || !application) return false;
    return (
      application.department === "both" &&
      user.designation === "tahsildar" &&
      application.status === "under_review"
    );
  };

  const isAtFinalReviewStage = () => {
    if (!application) return false;
    const appDept = application.department || "revenue";
    // Final stage is now "order_issued" for all departments
    if (appDept === "both" || appDept === "survey") {
      return application.status === "order_issued";
    }
    return application.status === "order_issued";
  };

  const canFarmerModify = () => {
    if (!user || !application) return false;
    return (
      user.role === "farmer" &&
      application.farmer_id === user.id &&
      application.status === "submitted"
    );
  };

  // Data loading
  useEffect(() => {
    loadApplicationData();
  }, [id]);

  const loadApplicationData = async () => {
    try {
      const [appRes, commentsRes, docsRes] = await Promise.all([
        api.get(`/applications/${id}`),
        api.get(`/applications/${id}/comments`),
        api.get(`/applications/${id}/documents`),
      ]);
      setApplication(appRes.data);
      setComments(commentsRes.data);
      setDocuments(docsRes.data);
      
      // Fetch valid statuses for officials (RBAC)
      if (user?.role === "official") {
        try {
          const validStatusRes = await api.get(`/applications/${id}/valid-next-statuses`);
          setValidStatusData(validStatusRes.data);
        } catch (err) {
          // If RBAC endpoint fails, continue without it (backwards compatibility)
          console.warn("Failed to fetch valid statuses:", err);
          setValidStatusData(null);
        }
      }
    } catch (error) {
      toast.error(t("common.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  // Action handlers
  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.put(`/applications/${id}/status`, { status: newStatus });
      toast.success(t("common.statusUpdated"));
      loadApplicationData();
    } catch (error) {
      toast.error(t("common.failedToUpdateStatus"));
    }
  };

  const handleApproveReject = async (status, comment) => {
    try {
      await api.put(`/applications/${id}/status`, { status, comment });
      toast.success(t("common.statusUpdated"));
      loadApplicationData();
    } catch (error) {
      toast.error(t("common.failedToUpdateStatus"));
    }
  };

  const handleRevenueApproval = async () => {
    try {
      await api.put(`/applications/${id}/status`, {
        status: "revenue_approved",
        comment: "Revenue phase approved, moving to Survey department",
      });
      toast.success(t("status.movedToSurvey"));
      loadApplicationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve revenue phase");
    }
  };

  const openTransferDialog = async () => {
    const targetDept = getTargetDepartment();
    if (!targetDept) return;
    try {
      // Fetch officials from target department
      const response = await api.get(`/officials/assignable?department=${targetDept}`);
      
      // Filter to show only super authorities of the target department
      // Revenue super authority: tahsildar
      // Survey super authorities: ddlr, adlr
      const superAuthorityDesignations = targetDept === 'revenue' 
        ? ['tahsildar'] 
        : ['ddlr', 'adlr'];
      
      const superAuthorities = response.data.filter(official => 
        superAuthorityDesignations.includes(official.designation)
      );
      
      setTransferOfficials(superAuthorities);
      setShowTransferDialog(true);
    } catch (error) {
      toast.error(t("common.failedToLoad"));
    }
  };

  const handleTransferDepartment = async () => {
    const targetDept = getTargetDepartment();
    if (!targetDept) return;
    
    // Validate transfer note is provided
    if (!transferNote?.trim()) {
      toast.error(t("applicationDetail.transferNoteRequired") || "Please provide a transfer note");
      return;
    }

    setTransferLoading(true);
    try {
      await api.put(`/applications/${id}/transfer-department`, {
        target_department: targetDept,
        target_official_id: selectedTransferOfficial === "none" ? null : (selectedTransferOfficial || null),
        transfer_note: transferNote.trim(),
      });
      toast.success(
        t("applicationDetail.transferSuccess", {
          department:
            targetDept === "survey" ? t("department.survey") : t("department.revenue"),
        })
      );
      setShowTransferDialog(false);
      setSelectedTransferOfficial("");
      setTransferNote("");
      loadApplicationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("applicationDetail.transferFailed"));
    } finally {
      setTransferLoading(false);
    }
  };

  // Open Assign to Official dialog
  const openAssignOfficialDialog = async () => {
    try {
      // Get assignable officials based on user's department
      const dept = user?.designation === "tahsildar" ? "revenue" : "survey";
      const response = await api.get(`/officials/assignable?department=${dept}`);
      setAssignableOfficials(response.data);
      setOfficialSearchQuery("");
      setSelectedAssignOfficial("");
      setAssignNote("");
      setAssignToStatus("");
      setShowAssignOfficialDialog(true);
    } catch (error) {
      toast.error(t("common.failedToLoad") || "Failed to load officials");
    }
  };

  // Handle assigning application to an official
  const handleAssignOfficial = async () => {
    if (!selectedAssignOfficial) {
      toast.error(t("officialsManagement.selectOfficial") || "Please select an official");
      return;
    }

    setAssignOfficialLoading(true);
    try {
      const payload = {
        official_id: selectedAssignOfficial,
        assign_note: assignNote?.trim() || null,
        assign_to_status: assignToStatus || null
      };
      
      await api.put(`/applications/${id}/assign`, payload);
      toast.success(t("officialsManagement.assignedSuccessfully") || "Application assigned successfully");
      setShowAssignOfficialDialog(false);
      setSelectedAssignOfficial("");
      setAssignNote("");
      setAssignToStatus("");
      loadApplicationData();
    } catch (error) {
      console.error("Assign error:", error);
      toast.error(error.response?.data?.detail || t("officialsManagement.errorAssigning") || "Failed to assign application");
    } finally {
      setAssignOfficialLoading(false);
    }
  };

  const handleOverrideDecision = async () => {
    if (!overrideStatus || !overrideReason.trim()) {
      toast.error(t("gok.overrideReasonRequired"));
      return;
    }

    setOverrideLoading(true);
    try {
      await api.put(`/applications/${id}/override`, {
        new_status: overrideStatus,
        reason: overrideReason.trim(),
      });
      toast.success(t("gok.overrideSuccess"));
      setShowOverrideDialog(false);
      setOverrideStatus("");
      setOverrideReason("");
      loadApplicationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("gok.overrideFailed"));
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleAssignSection = async () => {
    // For Survey users, survey office type is required (instead of section/circle)
    if (isSurveyUser) {
      if (!selectedSurveyOfficeType) {
        toast.error(t("surveyOffice.selectOfficeType") || "Please select an office type");
        return;
      }
    } else {
      // For Revenue users
      if (!selectedSection) {
        toast.error(t("common.selectSection") || "Please select a section");
        return;
      }
      if (showCircleField && !selectedCircle) {
        toast.error(t("common.selectBoth") || "Please select both section and circle");
        return;
      }
    }

    setSectionLoading(true);
    try {
      const requestData = isSurveyUser
        ? {
            survey_office_type: selectedSurveyOfficeType,
            comment: sectionComment || null,
          }
        : {
            section: selectedSection,
            circle: showCircleField ? selectedCircle : null,
            comment: sectionComment || null,
          };
      
      await api.put(`/applications/${id}/assign-section`, requestData);
      toast.success(
        isSurveyUser 
          ? (t("surveyOffice.assignSuccess") || "Office type assigned successfully")
          : (t("section.assignSuccess") || "Section assigned successfully")
      );
      setShowSectionDialog(false);
      setSelectedSection("");
      setSelectedCircle("");
      setSelectedSurveyOfficeType("");
      setSectionComment("");
      loadApplicationData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || t("section.assignFailed") || "Failed to assign"
      );
    } finally {
      setSectionLoading(false);
    }
  };

  const openEditDialog = () => {
    if (!application) return;
    setEditFormData({
      land_area: application.land_area,
      land_survey_number: application.land_survey_number,
      purpose: application.purpose,
      location: application.location,
      district: application.district,
      taluk: application.taluk,
      hobli: application.hobli,
      village: application.village,
    });
    setShowEditDialog(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditApplication = async () => {
    setEditLoading(true);
    try {
      await api.put(`/applications/${id}`, editFormData);
      toast.success(t("applicationDetail.editSuccess"));
      setShowEditDialog(false);
      loadApplicationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("applicationDetail.editFailed"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteApplication = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/applications/${id}`);
      toast.success(t("applicationDetail.deleteSuccess"));
      setShowDeleteDialog(false);
      navigate("/farmer/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("applicationDetail.deleteFailed"));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return <LoadingOverlay />;
  }

  // Not found state
  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {t("application.notFound")}
          </h2>
          <Button onClick={() => navigate(-1)}>{t("application.back")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 pb-16 md:pb-0">
      {/* Header */}
      <ApplicationHeader
        application={application}
        user={user}
        canFarmerModify={canFarmerModify()}
        onEditClick={openEditDialog}
        onDeleteClick={() => setShowDeleteDialog(true)}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Application Info */}
            <ApplicationInfoCard application={application} />

            {/* Status Timeline */}
            <StatusTimeline
              application={application}
              user={user}
              statusFlow={getStatusFlow()}
              bothStatusFlow={bothStatusFlow}
              getCurrentPhase={getCurrentPhase}
              canApprove={canApprove()}
              canApproveRevenuePhase={canApproveRevenuePhase()}
              isAtFinalReviewStage={isAtFinalReviewStage()}
              isGOKOfficial={isGOKOfficial()}
              onStatusUpdate={handleStatusUpdate}
              onApproveReject={handleApproveReject}
              onRevenueApproval={handleRevenueApproval}
              onOverrideClick={() => setShowOverrideDialog(true)}
              validStatusData={validStatusData}
            />

            {/* Comments */}
            <CommentsSection
              applicationId={id}
              comments={comments}
              onCommentAdded={loadApplicationData}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Actions Card */}
            <ActionsCard
              application={application}
              user={user}
              canAssignSection={canAssignSection()}
              canTransferDepartment={canTransferDepartment()}
              canAssignOfficial={canAssignOfficial()}
              getCurrentPhase={getCurrentPhase}
              getTargetDepartment={getTargetDepartment}
              onAssignSectionClick={() => setShowSectionDialog(true)}
              onTransferClick={openTransferDialog}
              onAssignOfficialClick={openAssignOfficialDialog}
              isSurveyUser={isSurveyUser}
            />

            {/* Documents */}
            <DocumentsSection
              applicationId={id}
              documents={documents}
              onDocumentUploaded={loadApplicationData}
            />

            {/* Messages */}
            <MessagePanel
              applicationId={id}
              currentUser={user}
              farmerId={application.farmer_id}
              officialId={application.assigned_official}
            />
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <TransferDialog
        open={showTransferDialog}
        onOpenChange={(open) => {
          setShowTransferDialog(open);
          if (!open) {
            setTransferNote("");
            setSelectedTransferOfficial("");
          }
        }}
        application={application}
        transferOfficials={transferOfficials}
        selectedTransferOfficial={selectedTransferOfficial}
        setSelectedTransferOfficial={setSelectedTransferOfficial}
        transferNote={transferNote}
        setTransferNote={setTransferNote}
        transferLoading={transferLoading}
        getTargetDepartment={getTargetDepartment}
        onTransfer={handleTransferDepartment}
      />

      <OverrideDialog
        open={showOverrideDialog}
        onOpenChange={setShowOverrideDialog}
        application={application}
        overrideStatus={overrideStatus}
        setOverrideStatus={setOverrideStatus}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        overrideLoading={overrideLoading}
        onOverride={handleOverrideDecision}
      />

      <SectionDialog
        open={showSectionDialog}
        onOpenChange={setShowSectionDialog}
        application={application}
        sections={sections}
        circles={circles}
        surveyOfficeTypes={surveyOfficeTypes}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        selectedCircle={selectedCircle}
        setSelectedCircle={setSelectedCircle}
        selectedSurveyOfficeType={selectedSurveyOfficeType}
        setSelectedSurveyOfficeType={setSelectedSurveyOfficeType}
        sectionComment={sectionComment}
        setSectionComment={setSectionComment}
        sectionLoading={sectionLoading}
        onAssign={handleAssignSection}
        showCircleField={showCircleField}
        isSurveyUser={isSurveyUser}
      />

      <EditDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        editFormData={editFormData}
        onFormChange={handleEditFormChange}
        editLoading={editLoading}
        onSave={handleEditApplication}
      />

      <DeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        application={application}
        deleteLoading={deleteLoading}
        onDelete={handleDeleteApplication}
      />

      {/* Assign to Official Dialog - Two Column Layout */}
      <AssignOfficialModal
        open={showAssignOfficialDialog}
        onOpenChange={(open) => {
          setShowAssignOfficialDialog(open);
          if (!open) {
            setOfficialSearchQuery("");
            setSelectedAssignOfficial("");
          }
        }}
        title={t("officialsManagement.assignApplication") || "Assign Application"}
        description={
          application
            ? `${t("officialsManagement.assignApplicationDesc") || "Assign application"} #${application.application_id || application.land_survey_number}`
            : undefined
        }
        officeOfficials={filteredOfficeOfficials}
        fieldOfficials={filteredFieldOfficials}
        allOfficials={assignableOfficials}
        selectedOfficialId={selectedAssignOfficial}
        onSelectOfficial={setSelectedAssignOfficial}
        searchQuery={officialSearchQuery}
        onSearchChange={setOfficialSearchQuery}
        assignNote={assignNote}
        onAssignNoteChange={setAssignNote}
        assignToStatus={assignToStatus}
        onAssignToStatusChange={setAssignToStatus}
        workflowStatuses={workflowStatuses}
        isSurveyUser={isSurveyUser}
        loading={assignOfficialLoading}
        onConfirm={handleAssignOfficial}
        onCancel={() => {
          setShowAssignOfficialDialog(false);
          setOfficialSearchQuery("");
          setSelectedAssignOfficial("");
          setAssignNote("");
          setAssignToStatus("");
        }}
        confirmLabel={t("common.assign") || "Assign"}
        testIdPrefix="detail-assign"
        currentAssignment={application?.assigned_official_name}
      />
    </div>
  );
}
